import { TREATMENT_OPTIONS, TREATMENT_OPTIONS_TYPE } from './treatmentOptions'
import { PROVIDER_TYPES, ProviderEnums, ProviderType } from '../../../models/providers/Providers'

const {
  COACHING,
  COACHING_FOR_PARENTS,
  LIVE_MESSAGING_COACHING,
  DIRECT_ACCESS_MEDS,
  BLENDED_CARE_MEDS,
  INDIVIDUAL_THERAPY,
  FAMILY_THERAPY,
  COUPLES_THERAPY,
  SELF_CARE_APPS,
  FAMILY_THERAPY_CHILD,
  INDIVIDUAL_THERAPY_CHILD,
  DIRECT_ACCESS_MEDS_CHILD,
  GUIDED_SELF_CARE,
  ALCOHOL_USE_DISORDER,
  CLINICAL_LEAVE_EVALUATION,
  NEURODIVERSITY_SKILLS_COACHING,
  CAREGIVER_TRAINING_FOR_NEURO,
} = TREATMENT_OPTIONS

const {
  [ProviderEnums.COACH]: COACH,
  [ProviderEnums.THERAPIST]: THERAPIST,
  [ProviderEnums.PROVIDER]: PROVIDER,
  [ProviderEnums.LYRAPRESCRIBER]: LYRAPRESCRIBER,
  [ProviderEnums.LYRACLINICALLEAVEEVALUATOR]: LYRACLINICALLEAVEEVALUATOR,
} = PROVIDER_TYPES

export const COLLOQUIAL_STRINGS_FOR_TREATMENT_OPTIONS: {
  [key in TREATMENT_OPTIONS_TYPE]?: string
} = {
  [COACHING]: 'Coaching',
  [LIVE_MESSAGING_COACHING]: 'Live Messaging Coaching',
  [GUIDED_SELF_CARE]: 'Guided Self-Care',
  [DIRECT_ACCESS_MEDS]: 'Medication Consult - DA Meds',
  [BLENDED_CARE_MEDS]: 'Medication Consult - BC Meds',
  [INDIVIDUAL_THERAPY]: 'Individual Therapy',
  [FAMILY_THERAPY]: 'Family Therapy',
  [COUPLES_THERAPY]: 'Couples Therapy',
  [SELF_CARE_APPS]: 'Mental Wellness Tools',
  [FAMILY_THERAPY_CHILD]: 'Family Therapy - Child',
  [INDIVIDUAL_THERAPY_CHILD]: 'Child and Adolescent Therapy',
  [DIRECT_ACCESS_MEDS_CHILD]: 'Medication Consult - Child',
  [ALCOHOL_USE_DISORDER]: 'Alcohol Use Disorder',
  [CLINICAL_LEAVE_EVALUATION]: 'Clinical Leave Evaluation',
  [COACHING_FOR_PARENTS]: 'Coaching for Parents',
}

// After a user selects a treatment option, we want to tailor the terminology used on subsequent
// pages to refer to the recommended care givers for the treatment in question. In some cases this
// maps to a specific provider type, but in others like Direct Access Meds, where multiple
// types of providers surface, we instead default to the generic "provider" type
export const TRIAGE_TREATMENT_OPTIONS_TO_PROVIDER_TYPES_FOR_DISPLAY: Partial<
  Record<TREATMENT_OPTIONS_TYPE, ProviderType>
> = {
  [BLENDED_CARE_MEDS]: LYRAPRESCRIBER,
  [COACHING]: COACH,
  [COACHING_FOR_PARENTS]: COACH,
  [DIRECT_ACCESS_MEDS]: PROVIDER,
  [DIRECT_ACCESS_MEDS_CHILD]: PROVIDER,
  [INDIVIDUAL_THERAPY]: THERAPIST,
  [FAMILY_THERAPY]: THERAPIST,
  [COUPLES_THERAPY]: THERAPIST,
  [FAMILY_THERAPY_CHILD]: THERAPIST,
  [INDIVIDUAL_THERAPY_CHILD]: THERAPIST,
  [GUIDED_SELF_CARE]: COACH,
  [CLINICAL_LEAVE_EVALUATION]: LYRACLINICALLEAVEEVALUATOR,
  [NEURODIVERSITY_SKILLS_COACHING]: COACH,
  [CAREGIVER_TRAINING_FOR_NEURO]: PROVIDER,
}

export const PROGRAM_TAXONOMY = {
  clientele: {
    individual: 'Individual',
    couples: 'Couples',
    family: 'Family',
    group: 'Group',
  },
  treatment: {
    therapy: 'Therapy',
    medicationManagement: 'MedicationManagement',
    coaching: 'Coaching',
    assessment: 'Assessment',
  },
  offering: {
    default: 'Default',
    singleSession: 'SingleSession',
    alcoholUseDisorder: 'AlcoholUseDisorder',
    clinicalLeave: 'ClinicalLeave',
    fellowship: 'Fellowship',
    parenting: 'Parenting',
    neurodiversitySkills: 'NeurodiversitySkills',
    caregiverTrainingForNeuro: 'CaregiverTrainingForNeuro',
    dbt: 'DBT',
    dbtTeens: 'DBTTeens',
    performance: 'Performance',
    manager: 'Manager',
  },
  partner: {
    blendedCare: 'BlendedCare',
    directAccess: 'DirectAccess',
    icas: 'Icas',
    // CPR-2984: Remove this CTPO after pilot ends
    google: 'Google',
  },
} as const

export enum ProgramCustomerPropertyId {
  alcoholUseDisorderTherapy = 'alcoholUseDisorderTherapy',
  blendedCareMeds = 'blendedCareMeds',
  clinicalLeaveEvaluation = 'clinicalLeaveEvaluation',
  coachingForParents = 'coachingForParents',
  considerLyraTherapy = 'considerLyraTherapy',
  considerMeds = 'considerMeds',
  dialecticalBehaviorTherapy = 'dialecticalBehaviorTherapy',
  dialecticalBehaviorTherapyTeens = 'dialecticalBehaviorTherapyTeens',
  directAccessTherapy = 'directAccessTherapy',
  empty = '',
  libraryDisabled = 'libraryDisabled',
  guidedSelfCareEnabled = 'guidedSelfCareEnabled',
  icas = 'icas',
  mystrength_accesscode = 'mystrength_accesscode',
  stressManagement = 'stressManagement',
  nicotineQuitline = 'nicotineQuitline',
  directAccessCouplesTherapy = 'directAccessCouplesTherapy',
  directAccessFamilyTherapy = 'directAccessFamilyTherapy',
  directAccessIndividualTherapy = 'directAccessIndividualTherapy',
  directAccessIndividualChildTherapy = 'directAccessIndividualChildTherapy',
  directAccessIndividualAdolescentTherapy = 'directAccessIndividualAdolescentTherapy',
  neurodiversitySkillsCoaching = 'neurodiversitySkillsCoaching',
  caregiverTrainingForNeuro = 'caregiverTrainingForNeuro',
  neuropsychologicalEvaluations = 'neuropsychologicalEvaluations',
  managerCoaching = 'managerCoaching',
}

export const ousPrograms = [ProgramCustomerPropertyId.directAccessTherapy, ProgramCustomerPropertyId.stressManagement]

export const DEFAULT_PROGRAM_TAXONOMY = {
  clientele: PROGRAM_TAXONOMY.clientele.individual,
  treatment: PROGRAM_TAXONOMY.treatment.therapy,
  partner: PROGRAM_TAXONOMY.partner.blendedCare,
  offering: PROGRAM_TAXONOMY.offering.default,
}

export const COACHING_TREATMENT_OPTIONS = [
  TREATMENT_OPTIONS.COACHING,
  TREATMENT_OPTIONS.GUIDED_SELF_CARE,
  TREATMENT_OPTIONS.COACHING_FOR_PARENTS,
  TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING,
  TREATMENT_OPTIONS.MANAGER_COACHING,
]

// NOTE: Before adding a new DirectLinkIntent, contact the ML team as this value informs provider ranking
export enum DirectLinkIntent {
  MEDS_CONSULT = 'DIRECT_LINK_MEDS_CONSULT',
  PARENTING = 'DIRECT_LINK_INTENT_PARENTING',
  OUTPATIENT_MEDS = 'DIRECT_LINK_OUTPATIENT_MEDICATIONS',
  AUD = 'DIRECT_LINK_ALCOHOL_USE_SUPPORT',
  FAMILY_SUPPORT = 'DIRECT_LINK_INTENT_FAMILY_SUPPORT',
  COUPLES_THERAPY = 'DIRECT_LINK_INTENT_COUPLES',
  ONSITE_THERAPY = 'DIRECT_LINK_INTENT_ONSITE_THERAPY',
  DIRECT_LINK_INTENT_HIGH_RISK = 'DIRECT_LINK_INTENT_HIGH_RISK',
  COVERAGE_INFORMATION = 'DIRECT_LINK_INTENT_COVERAGE_INFO',
  DBT = 'DIRECT_LINK_INTENT_DBT',
  DBTT = 'DIRECT_LINK_INTENT_DBTT',
  CGT = 'DIRECT_LINK_INTENT_CGT',
  NSC = 'DIRECT_LINK_INTENT_NSC',
  HEALTHY_LIFESTYLE = 'DIRECT_LINK_INTENT_HEALTHY_LIFESTYLE',
  DIRECT_LINK_CONCURRENT_CARE_REFERRAL = 'DIRECT_LINK_CONCURRENT_CARE_REFERRAL',
}
